import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
} from "reactstrap";
import { withTranslation } from "react-i18next";
import ReactCountryFlag from "react-country-flag";

import i18n from "../../../i18n";
import languages from "../../../common/languages";
import {
  getStoredAppLanguage,
  setAppLanguage,
} from "../../../helpers/i18nLanguage";

function LanguageDropdown({ t }) {
  const [selectedLang, setSelectedLang] = useState(() =>
    getStoredAppLanguage()
  );
  const [menu, setMenu] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);

  useEffect(() => {
    const sync = (lng) => {
      const code = (lng || getStoredAppLanguage()).split("-")[0];
      setSelectedLang(languages[code] ? code : "en");
    };
    sync(i18n.language);
    i18n.on("languageChanged", sync);
    return () => i18n.off("languageChanged", sync);
  }, []);

  useEffect(() => {
    if (!menu) {
      setQuery("");
      return;
    }
    const id = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [menu]);

  const changeLanguageAction = (lang) => {
    setAppLanguage(lang);
    setSelectedLang(lang);
    setMenu(false);
  };

  const selectedLanguage = languages[selectedLang] || languages.en;

  const languageEntries = useMemo(() => {
    const q = query.trim().toLowerCase();
    const entries = Object.entries(languages);
    if (!q) return entries;
    return entries.filter(([code, meta]) => {
      const label = String(meta?.label || "").toLowerCase();
      return label.includes(q) || code.toLowerCase().includes(q);
    });
  }, [query]);

  const renderFlag = (countryCode, alt) => {
    if (!countryCode) return null;
    return (
      <ReactCountryFlag
        countryCode={countryCode}
        svg
        className="lang-dropdown__flag"
        title={alt}
        aria-hidden="true"
      />
    );
  };

  return (
    <Dropdown
      isOpen={menu}
      toggle={() => setMenu((open) => !open)}
      className="lang-dropdown d-inline-block"
    >
      <DropdownToggle
        className="lang-dropdown__toggle"
        tag="button"
        type="button"
        aria-label={t("languageDropdownAria", "Select language")}
        aria-haspopup="listbox"
        aria-expanded={menu}
      >
        <span className="lang-dropdown__toggle-main">
          {selectedLanguage?.countryCode &&
            renderFlag(selectedLanguage.countryCode, selectedLanguage.label)}
          <span className="lang-dropdown__toggle-text">
            <span className="lang-dropdown__toggle-label">
              {selectedLanguage?.label ||
                t("languageDropdownFallback", "Language")}
            </span>
          </span>
        </span>
        <i
          className={`bx bx-chevron-${
            menu ? "up" : "down"
          } lang-dropdown__chevron`}
          aria-hidden="true"
        />
      </DropdownToggle>

      <DropdownMenu
        className="lang-dropdown__menu dropdown-menu-end"
        end
        role="listbox"
        aria-label={t("languageDropdownListAria", "Available languages")}
      >
        <div
          className="lang-dropdown__search"
          onClick={(e) => e.stopPropagation()}
        >
          <i
            className="bx bx-search lang-dropdown__search-icon"
            aria-hidden="true"
          />
          <Input
            innerRef={searchRef}
            type="search"
            bsSize="sm"
            className="lang-dropdown__search-input"
            placeholder={t("languageDropdownSearch", "Search languages...")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            autoComplete="off"
          />
        </div>

        <div className="lang-dropdown__list">
          {languageEntries.length === 0 ? (
            <div className="lang-dropdown__empty">
              {t("languageDropdownNoResults", "No languages found")}
            </div>
          ) : (
            languageEntries.map(([key, meta]) => {
              const active = key === selectedLang;
              return (
                <DropdownItem
                  key={key}
                  tag="button"
                  type="button"
                  onClick={() => changeLanguageAction(key)}
                  className={`lang-dropdown__item${active ? " is-active" : ""}`}
                  role="option"
                  aria-selected={active}
                >
                  <span className="lang-dropdown__item-main">
                    {meta.countryCode &&
                      renderFlag(meta.countryCode, meta.label)}
                    <span className="lang-dropdown__item-label">
                      {meta.label}
                    </span>
                  </span>
                  <span className="lang-dropdown__item-meta">
                    <span className="lang-dropdown__item-code">
                      {String(key).toUpperCase()}
                    </span>
                    {active ? (
                      <i
                        className="bx bx-check lang-dropdown__check"
                        aria-hidden="true"
                      />
                    ) : null}
                  </span>
                </DropdownItem>
              );
            })
          )}
        </div>
      </DropdownMenu>
    </Dropdown>
  );
}

export default withTranslation()(LanguageDropdown);
